import { useCallback, useMemo, useState } from 'react';
import createClient from '@eid-easy/eideasy-browser-client';
import * as Sentry from '@sentry/react';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import { EidEasySignStatus } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal.types';
import { EidEasySigningMethod } from '@dealstack/shared/lib/common/eideasy/signingMethods';
import type { EidEasyCompleteRes } from '@dealstack/shared/lib/generated';
import {
	buildEndpointBase,
	FTN_CALLBACK_PATH,
	isEidEasySandbox,
	isUserInitiatedError,
	SMART_ID_TIMEOUT_MS,
	startFtnTabSigning,
} from './useEidEasySign.utils';
import { useEidEasySignCore } from './useEidEasySignCore';
import { SigningTransport } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useEidEasySignCore.types';
import type {
	EidEasyPrepareResponse,
	EidEasyCompleteResponse,
	PendingSignRequest,
	UseEidEasySignOptions,
} from './useEidEasySign.types';

export const useEidEasySign = ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	onComplete,
}: UseEidEasySignOptions) => {
	const [pendingRequest, setPendingRequest] = useState<PendingSignRequest | null>(null);
	const [selectedMethod, setSelectedMethod] = useState<EidEasySigningMethod | null>(null);

	const onSignFinished = useCallback(
		(result?: EidEasyCompleteRes) => {
			setPendingRequest(null);
			onComplete?.(result);
		},
		[onComplete]
	);

	// Authenticated, deal-scoped REST transport. Each prepare carries the
	// `signatureId`; signing is only possible once a request is pending.
	const transport = useMemo<SigningTransport>(() => {
		const basePath = () => {
			if (!pendingRequest) {
				throw new Error('No pending sign request');
			}
			return buildEndpointBase(dealId, programmeId, pendingRequest.instanceId);
		};
		return {
			canSign: () => pendingRequest !== null,
			prepare: async (method, extras) => {
				const result = await makeRequest<EidEasyPrepareResponse>({
					endpoint: `${basePath()}/eid-easy/prepare`,
					method: HTTP_METHOD.POST,
					body: JSON.stringify({
						signatureId: pendingRequest?.signatureId,
						method,
						...extras,
					}),
				});
				return result.payload;
			},
			complete: async (docId, extras) => {
				const result = await makeRequest<EidEasyCompleteResponse>({
					endpoint: `${basePath()}/eid-easy/complete`,
					method: HTTP_METHOD.POST,
					body: JSON.stringify({ docId, ...extras }),
				});
				return result.payload;
			},
		};
	}, [dealId, programmeId, pendingRequest]);

	// Admin/in-app policy: tag `signingFlow: 'single'` and don't alert Sentry for
	// user-initiated cancellations (tab closed, Smart-ID USER_REFUSED, …).
	const captureError = useCallback((err: unknown, phase: string) => {
		if (!isUserInitiatedError(err)) {
			Sentry.captureException(err, { tags: { signingFlow: 'single', phase } });
		}
	}, []);

	const {
		status,
		challengeCode,
		error,
		setStatus,
		setError,
		setChallengeCode,
		unmountedRef,
		runFlow,
		pendingContext,
		setPendingContext,
		pendingMethodSelection,
		setPendingMethodSelection,
		capturedSignature,
		setCapturedSignature,
		confirmFreja,
		confirmOtp,
		submitOtpCode,
		resendOtpCode,
		otpVerifying,
		otpCodeError,
		confirmSignature,
		onSignatureConfirmed: coreOnSignatureConfirmed,
		resetOtpContext,
		otpChannel,
		otpMaskedContact,
	} = useEidEasySignCore({ transport, captureError, onComplete: onSignFinished });

	// ─── Smart-ID: SDK-based (vs public flow's hosted-page poll) ─────────────

	/** Runs the actual Smart-ID SDK flow with the captured signature. */
	const runSmartIdFlow = useCallback(
		async (idcode: string, countryCode: string, signatureBase64: string) => {
			if (!pendingRequest) {
				return;
			}
			setStatus(EidEasySignStatus.PREPARING);
			setError(null);
			setChallengeCode(null);

			await runFlow('smart-id', async () => {
				const { docId, clientId, baseUrl, mockMode } = await transport.prepare(
					EidEasySigningMethod.SMART_ID,
					{ drawnSignatureBase64: signatureBase64 }
				);

				setStatus(EidEasySignStatus.AWAITING_SIGN);

				if (!mockMode) {
					const client = createClient({
						clientId,
						docId,
						countryCode,
						language: 'en',
						sandbox: isEidEasySandbox(baseUrl),
					});

					await new Promise<void>((resolve, reject) => {
						const timeout = setTimeout(() => {
							reject(new Error('TIMEOUT'));
						}, SMART_ID_TIMEOUT_MS);

						client.signature.smartIdSignature.start({
							idcode,
							countryCode,
							waitForChallengeCode: false,
							started: (startResult) => {
								if (startResult.data?.challenge) {
									setChallengeCode(startResult.data.challenge);
								}
							},
							onTryComplete: (tryResult) => {
								if (tryResult.data?.challenge) {
									setChallengeCode(tryResult.data.challenge);
								}
							},
							success: () => {
								clearTimeout(timeout);
								setChallengeCode(null);
								resolve();
							},
							fail: (failError) => {
								clearTimeout(timeout);
								setChallengeCode(null);
								reject(
									new Error(
										failError instanceof Error
											? failError.message
											: String(failError)
									)
								);
							},
						});
					});
				}

				if (unmountedRef.current) {
					return;
				}
				setStatus(EidEasySignStatus.COMPLETING);
				const result = await transport.complete(docId);

				if (unmountedRef.current) {
					return;
				}
				setStatus(EidEasySignStatus.DONE);
				onSignFinished(result);
			});
		},
		[
			pendingRequest,
			transport,
			runFlow,
			setStatus,
			setError,
			setChallengeCode,
			unmountedRef,
			onSignFinished,
		]
	);

	/**
	 * Called from the Smart-ID input step after the user's signature has already
	 * been captured in `capturedSignature`. Calls `runSmartIdFlow` directly with
	 * the cached signature — no second SETUP_SIGNATURE pass.
	 */
	const confirmSmartId = useCallback(
		(idcode: string, countryCode: string) => {
			if (!pendingRequest) {
				return;
			}
			if (!capturedSignature) {
				// No cached signature — this shouldn't happen in the normal flow.
				// Fall back to the old path: set pendingContext and advance to SETUP_SIGNATURE.
				setPendingContext({ method: EidEasySigningMethod.SMART_ID, idcode, countryCode });
				setStatus(EidEasySignStatus.SETUP_SIGNATURE);
				return;
			}
			const sig = capturedSignature;
			setCapturedSignature(null);
			void runSmartIdFlow(idcode, countryCode, sig);
		},
		[
			pendingRequest,
			capturedSignature,
			setCapturedSignature,
			setPendingContext,
			setStatus,
			runSmartIdFlow,
		]
	);

	// ─── FTN: SDK tab flow ────────────────────────────────────────────────────

	/** Runs the actual FTN SDK tab flow with the captured signature. */
	const runFtnFlow = useCallback(
		async (signatureBase64: string) => {
			if (!pendingRequest) {
				return;
			}
			const ftnCallbackUrl = `${window.location.origin}${FTN_CALLBACK_PATH}`;

			setStatus(EidEasySignStatus.PREPARING);
			setError(null);

			await runFlow('ftn', async () => {
				const { docId, clientId, baseUrl, mockMode } = await transport.prepare(
					EidEasySigningMethod.FTN,
					{
						signatureRedirect: ftnCallbackUrl,
						drawnSignatureBase64: signatureBase64,
					}
				);

				setStatus(EidEasySignStatus.AWAITING_SIGN);

				if (!mockMode) {
					const client = createClient({
						clientId,
						docId,
						countryCode: 'FI',
						language: 'en',
						sandbox: isEidEasySandbox(baseUrl),
						redirectUri: ftnCallbackUrl,
					});

					await startFtnTabSigning(client);
				}

				if (unmountedRef.current) {
					return;
				}
				setStatus(EidEasySignStatus.COMPLETING);
				const result = await transport.complete(docId);

				if (unmountedRef.current) {
					return;
				}
				setStatus(EidEasySignStatus.DONE);
				onSignFinished(result);
			});
		},
		[pendingRequest, transport, runFlow, setStatus, setError, unmountedRef, onSignFinished]
	);

	// ─── Shared signature-confirmed handler (wraps core + adds Smart-ID/FTN) ──

	const onSignatureConfirmed = useCallback(
		async (signatureBase64: string) => {
			// If the user came via method-select → SETUP_SIGNATURE, handle FTN here
			// (Smart-ID, OTP, Freja are handled by the core's pendingMethodSelection path).
			if (pendingMethodSelection === EidEasySigningMethod.FTN) {
				setPendingMethodSelection(null);
				await runFtnFlow(signatureBase64);
				return;
			}

			// If pendingContext.method is Smart-ID or FTN (old path, e.g. from tests),
			// handle those here before delegating to the core.
			if (pendingContext?.method === EidEasySigningMethod.SMART_ID) {
				const ctx = pendingContext;
				setPendingContext(null);
				await runSmartIdFlow(ctx.idcode, ctx.countryCode, signatureBase64);
				return;
			}
			if (pendingContext?.method === EidEasySigningMethod.FTN) {
				setPendingContext(null);
				await runFtnFlow(signatureBase64);
				return;
			}

			// Delegate all other cases (Freja, OTP, redirect methods, pendingMethodSelection
			// for Smart-ID/OTP/Freja which advance to input steps) to the core handler.
			await coreOnSignatureConfirmed(signatureBase64);
		},
		[
			pendingMethodSelection,
			setPendingMethodSelection,
			pendingContext,
			setPendingContext,
			runSmartIdFlow,
			runFtnFlow,
			coreOnSignatureConfirmed,
		]
	);

	const startSign = useCallback(
		(signatureId: string, instanceIdOverride?: string, fileId?: string) => {
			const instanceId = instanceIdOverride ?? documentWorkflowInstanceId;
			if (!instanceId) {
				setError('Missing document workflow instance ID');
				setStatus(EidEasySignStatus.ERROR);
				return;
			}
			setPendingRequest({ signatureId, instanceId, fileId });
			setError(null);
			setChallengeCode(null);
			setSelectedMethod(null);
			resetOtpContext();
			setStatus(EidEasySignStatus.METHOD_SELECT);
		},
		[documentWorkflowInstanceId, resetOtpContext, setError, setStatus, setChallengeCode]
	);

	const confirmReview = useCallback(() => {
		setStatus(EidEasySignStatus.METHOD_SELECT);
	}, [setStatus]);

	const goBack = useCallback(() => {
		setStatus(EidEasySignStatus.METHOD_SELECT);
	}, [setStatus]);

	const selectMethod = useCallback(
		(method: EidEasySigningMethod) => {
			// Drop any OTP context carried over from a prior attempt in this modal
			// session — otherwise `confirmSignature` would route to the OTP complete
			// endpoint with a stale docId.
			if (method !== EidEasySigningMethod.OTP) {
				resetOtpContext();
			}
			// Clear any cached signature from a previous attempt.
			setCapturedSignature(null);
			setSelectedMethod(method);

			if (method === EidEasySigningMethod.DRAW) {
				setStatus(EidEasySignStatus.DRAW_SIGNATURE);
				return;
			}

			// All non-DRAW methods gate through SETUP_SIGNATURE immediately on method
			// select, before any method-specific input step.
			setPendingMethodSelection(method);
			setStatus(EidEasySignStatus.SETUP_SIGNATURE);
		},
		[resetOtpContext, setCapturedSignature, setPendingMethodSelection, setStatus]
	);

	const reset = useCallback(() => {
		setStatus(EidEasySignStatus.IDLE);
		setChallengeCode(null);
		setError(null);
		setPendingRequest(null);
		setPendingContext(null);
		setCapturedSignature(null);
		setPendingMethodSelection(null);
		setSelectedMethod(null);
		resetOtpContext();
	}, [
		resetOtpContext,
		setStatus,
		setError,
		setChallengeCode,
		setPendingContext,
		setCapturedSignature,
		setPendingMethodSelection,
	]);

	return {
		startSign,
		confirmReview,
		selectMethod,
		goBack,
		confirmSmartId,
		confirmFreja,
		confirmOtp,
		submitOtpCode,
		resendOtpCode,
		otpVerifying,
		otpCodeError,
		otpChannel,
		otpMaskedContact,
		confirmSignature,
		onSignatureConfirmed,
		reset,
		status,
		challengeCode,
		error,
		selectedMethod,
		isSigningInProgress:
			status === EidEasySignStatus.PREPARING ||
			status === EidEasySignStatus.AWAITING_SIGN ||
			status === EidEasySignStatus.COMPLETING,
		isModalOpen: status !== EidEasySignStatus.IDLE,
	};
};
